<script lang="ts" setup>
import { OtIcon, OtLozenge, OtNumberDisplay } from '@1trading/design-system';
import { computed, type PropType } from 'vue';
import { useI18n } from 'vue-i18n';

import { MarketType } from '@exchange/libs/market/service/src/lib/market-model';
import OrderStatus from '@exchange/libs/order/shared-components/src/lib/OrderStatus.vue';
import TimeFromNow from '@exchange/libs/order/shared-components/src/lib/TimeFromNow.vue';
import { MyOrdersListType } from '@exchange/libs/order/shared-model/src/lib/order-essentials';
import type { OrderWithTradesModel } from '@exchange/libs/order/shared-model/src/lib/order-model';
import { toastManagerInstance, OrderCancelFailedToast } from '@exchange/libs/toasts/src';

import useOrder from '../../useOrder';

const { t } = useI18n({ useScope: 'global' });

const props = defineProps({
  cancelOrder: { type: Function as PropType<(id: string) => Promise<void>>, required: true },
  order: { type: Object as PropType<OrderWithTradesModel>, required: true },
});

const listType = MyOrdersListType.open;
const { animation, market } = useOrder(props.order, listType);

const isFutures = computed(() => market.value?.type === MarketType.PERP);

const sideDisplay = computed(() => {
  if (isFutures.value) {
    return props.order.isBuy
      ? `${t('fundamentals.orders.side.buy')} / ${t('fundamentals.orders.side.long')}`
      : `${t('fundamentals.orders.side.sell')} / ${t('fundamentals.orders.side.short')}`;
  }
  return props.order.isBuy ? t('fundamentals.orders.side.buy') : t('fundamentals.orders.side.sell');
});

const orderItemClasses = computed(() => ({
  'status-open': props.order.status === 'OPEN',
  'status-triggered': props.order.status === 'STOP_TRIGGERED',
  'status-filled': props.order.status === 'FILLED',
  'status-filled-closed': props.order.status === 'FILLED_CLOSED',
  'status-filled-fully': props.order.status === 'FILLED_FULLY',
  'status-closing': props.order.status === 'CLOSING',
  'status-closed': props.order.status === 'CLOSED',
  'status-rejected': props.order.status === 'REJECTED',
  'status-filled-rejected': props.order.status === 'FILLED_REJECTED',
  'status-failed': props.order.status === 'FAILED',
}));

const handleCancelOrder = () =>
  props.cancelOrder(props.order.id).catch((error) =>
    toastManagerInstance.addToast({
      content: OrderCancelFailedToast,
      props: {
        errorCode: error.data ? error.data.error : undefined,
      },
    }),
  );
</script>

<template>
  <div
    class="open-orders-item-mobile"
    :class="orderItemClasses"
    :data-id="order.id"
    :style="{ 'animation-name': animation }"
  >
    <!-- {{ order }} -->
    <div class="open-orders-item-mobile__header">
      <div class="open-orders-item-mobile__market-info">
        <span class="open-orders-item-mobile__market-name">{{ order.marketName }}</span>
        <ot-lozenge
          v-if="isFutures"
          :filled="false"
          :text="$t('fundamentals.perpetual.short')"
          class="open-orders-item-mobile__lozenge"
        />
        <order-status
          class="open-orders-item-mobile__status"
          :order="order"
          :market="market"
        />
      </div>
      <x-button
        class="open-orders-item-mobile__cancel-button"
        size="3xs"
        variant="tertiary"
        @click="handleCancelOrder"
      >
        <ot-icon name="close-square" />
      </x-button>
    </div>
    <div class="open-orders-item-mobile__details">
      <div class="open-orders-item-mobile__column">
        <div class="open-orders-item-mobile__row">
          <span class="open-orders-item-mobile__label">{{ $t('modules.myOrders.header.time') }}:</span>
          <span class="open-orders-item-mobile__value">
            <time-from-now
              v-if="order.time"
              :date="order.time"
              :force-absolute-time="true"
            />
          </span>
        </div>
        <div class="open-orders-item-mobile__row">
          <span class="open-orders-item-mobile__label">{{ $t('modules.myOrders.header.amount') }}:</span>
          <span class="open-orders-item-mobile__value">
            <ot-number-display
              :number="order.amount"
              :precision="order.getAmountPrecision(market)"
              :suffix="order.amountSuffix"
            />
          </span>
        </div>
        <div class="open-orders-item-mobile__row">
          <span class="open-orders-item-mobile__label">{{ $t('modules.myOrders.header.total') }}:</span>
          <span class="open-orders-item-mobile__value">
            <ot-number-display
              :number="order.displayTotal"
              :precision="order.getTotalPrecision(market)"
              :suffix="order.quoteCurrencyCode"
            />
          </span>
        </div>
      </div>
      <div class="open-orders-item-mobile__column">
        <div class="open-orders-item-mobile__row">
          <span class="open-orders-item-mobile__label">{{ $t('modules.myOrders.header.side') }}:</span>
          <span
            class="open-orders-item-mobile__value"
            :class="{ buy: order.isBuy, sell: !order.isBuy }"
            >{{ sideDisplay }}</span
          >
        </div>
        <div class="open-orders-item-mobile__row">
          <span class="open-orders-item-mobile__label">{{ $t('modules.myOrders.header.priceShort') }}:</span>
          <span class="open-orders-item-mobile__value">
            <ot-number-display
              :number="order.displayPrice"
              :precision="order.getMarketPrecision(market)"
              :suffix="order.priceSuffix"
            />
          </span>
        </div>
        <div class="open-orders-item-mobile__row">
          <span class="open-orders-item-mobile__label">{{ $t('modules.myOrders.header.filled') }}:</span>
          <span class="open-orders-item-mobile__value">{{ order.filledAmount }} {{ order.amountSuffix }} / {{ order.filledPercent }}%</span>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
/* stylelint-disable */

.open-orders-item-mobile {
  display: flex;
  flex-direction: column;
  padding: 16px;
  margin-top: 8px;
  margin-bottom: 8px;
  border: 1px solid rgb(var(--v-theme-elevation-2));
  border-radius: var(--app-border-radius);
  background-color: rgb(var(--v-theme-elevation-1));
  animation-delay: 300ms;
  animation-duration: 1s;
  animation-fill-mode: forwards;
  animation-timing-function: ease-in;
  transition: all 200ms ease-in 300ms;

  &__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;
  }

  &__market-info {
    display: flex;
    align-items: center;
  }

  &__market-name {
    font-size: var(--font-size-md);
    font-weight: var(--font-weight-bold);
    color: rgb(var(--v-theme-text-primary));
  }

  &__lozenge {
    margin-left: 4px;
  }

  &__status {
    margin-left: 4px;
  }

  &__cancel-button {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    padding: 0;
    border: none;
    background-color: transparent;
    color: rgb(var(--v-theme-text-primary));
    cursor: pointer;
  }

  &__details {
    display: flex;
    justify-content: space-between;
    gap: 16px;
  }

  &__column {
    display: flex;
    flex-direction: column;
    gap: 8px;
    flex: 1;
  }

  &__row {
    display: flex;
    justify-content: space-between;
  }

  &__label {
    font-size: var(--font-size-sm);
    color: rgb(var(--v-theme-text-secondary));
  }

  &__value {
    font-size: var(--font-size-sm);
    font-weight: var(--font-weight-medium);
    color: rgb(var(--v-theme-text-primary));

    &.buy {
      color: rgb(var(--v-theme-success));
    }

    &.sell {
      color: rgb(var(--v-theme-error));
    }
  }

  // Add status-based styles
  &.market-suspended,
  &.market-in-maintenance,
  &.status-closing,
  &.status-closed,
  &.status-rejected,
  &.status-filled-rejected,
  &.status-failed {
    opacity: 0.5;
    pointer-events: none;
  }

  &.status-filled,
  &.status-filled-closed,
  &.status-filled-fully {
    background-color: rgba(var(--v-theme-elevation-2), 0.5);
  }
}
</style>
